import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { SidebarNavItem } from './sidebar-navigation.models';

export type SidebarNavigationItemVariant =
  | 'profile-settings'
  | 'profile-collapsed'
  | 'profile-sm'
  | 'profile-base'
  | 'backoffice-expanded'
  | 'backoffice-collapsed';

@Component({
  selector: 'app-sidebar-navigation-item',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  host: {
    class: 'block w-full',
  },
  template: `
    <button
      type="button"
      [class]="buttonClass"
      [title]="item.title || item.label"
      [attr.aria-label]="item.title || item.label"
      [attr.data-onboarding]="onboardingAttr"
      (click)="itemClick.emit()"
    >
      <fa-icon [icon]="item.icon" [class]="iconClass"></fa-icon>
      @if (!isIconOnly) {
        <span [class]="labelClass">{{ item.label }}</span>
        @if (item.lockedBadge) {
          <span
            class="ml-auto inline-flex items-center gap-1 rounded-full bg-[var(--color-brand-500)] px-2 py-0.5 text-[10px] font-bold text-white flex-shrink-0">
            <fa-icon [icon]="faLock" class="text-[9px]"></fa-icon>
            {{ item.lockedBadge }}
          </span>
        }
      }
    </button>
  `,
})
export class SidebarNavigationItemComponent {
  @Input({ required: true }) item!: SidebarNavItem;
  @Input() active = false;
  @Input() variant: SidebarNavigationItemVariant = 'profile-settings';
  @Output() readonly itemClick = new EventEmitter<void>();

  readonly faLock = faLock;

  get onboardingAttr(): string | null {
    switch (this.item.id) {
      case 'dashboard-home':
        return 'sidebar-home';
      case 'dashboard-agents':
        return 'sidebar-agents';
      case 'dashboard-knowledge':
        return 'sidebar-company';
      case 'settings-connections':
        return 'sidebar-settings-connections';
      case 'settings-agents-link':
        return 'sidebar-settings-agents';
      case 'settings-alerts-link':
        return 'sidebar-settings-alerts';
      case 'dashboard-settings':
        return 'sidebar-settings';
      default:
        return null;
    }
  }

  get isIconOnly(): boolean {
    return this.variant === 'backoffice-collapsed' || this.variant === 'profile-collapsed';
  }

  get buttonClass(): string {
    switch (this.variant) {
      case 'profile-settings':
        return this.active
          ? 'bg-[var(--color-brand-400)] cursor-pointer dark:bg-[var(--color-neutral-800)] flex items-center p-3 rounded-lg text-left w-full'
          : 'cursor-pointer dark:hover:bg-[var(--color-white)]/10 flex group hover:bg-[var(--color-brand-400)] hover:text-[var(--color-text-100)] items-center p-3 rounded-lg text-left transition-colors w-full';
      case 'profile-collapsed':
        return this.active
          ? 'w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer bg-[var(--color-brand-400)] dark:bg-[var(--color-neutral-800)] mx-auto'
          : 'w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer hover:bg-[var(--color-brand-400)] dark:hover:bg-[var(--color-white)]/10 transition-colors group mx-auto';
      case 'profile-sm':
      case 'profile-base':
        return this.active
          ? 'w-full flex items-center p-3 text-left bg-[var(--color-brand-400)] dark:bg-[var(--color-neutral-800)] rounded-lg cursor-pointer'
          : 'w-full flex items-center p-3 text-left hover:bg-[var(--color-brand-400)] hover:text-[var(--color-text-100)] dark:hover:bg-[var(--color-white)]/10 rounded-lg transition-colors cursor-pointer group';
      case 'backoffice-expanded':
        return this.active
          ? 'w-full h-11 flex items-center justify-start px-4 rounded-xl cursor-pointer group bg-[var(--color-brand-100)] dark:bg-[var(--color-neutral-800)]'
          : 'w-full h-11 flex items-center justify-start px-4 rounded-xl cursor-pointer group hover:bg-[var(--color-brand-100)] dark:hover:bg-[var(--color-white)]/10';
      case 'backoffice-collapsed':
        return this.active
          ? 'w-11 h-11 flex items-center justify-center rounded-xl cursor-pointer group bg-[var(--color-brand-100)] dark:bg-[var(--color-neutral-800)]'
          : 'w-11 h-11 flex items-center justify-center rounded-xl cursor-pointer group hover:bg-[var(--color-brand-100)] dark:hover:bg-[var(--color-white)]/10';
    }
  }

  get iconClass(): string {
    switch (this.variant) {
      case 'profile-settings':
        return this.active
          ? 'mr-3 text-[var(--color-text-100)] dark:text-[var(--color-white)] text-sm'
          : 'mr-3 text-[var(--color-gray-600)] group-hover:text-[var(--color-text-100)] dark:text-[var(--color-gray-400)] text-sm';
      case 'profile-collapsed':
        return this.active
          ? 'text-[var(--color-text-100)] dark:text-[var(--color-white)] text-sm'
          : 'text-[var(--color-gray-600)] group-hover:text-[var(--color-text-100)] dark:text-[var(--color-gray-400)] text-sm';
      case 'profile-sm':
        return this.active
          ? 'mr-3 text-[var(--color-text-100)] dark:text-[var(--color-white)] text-sm'
          : 'mr-3 text-[var(--color-gray-600)] group-hover:text-[var(--color-text-100)] dark:text-[var(--color-gray-400)] text-sm';
      case 'profile-base':
        return this.active
          ? 'mr-3 text-[var(--color-text-100)] dark:text-[var(--color-white)] text-base'
          : 'mr-3 text-[var(--color-gray-600)] group-hover:text-[var(--color-text-100)] dark:text-[var(--color-gray-400)] text-base';
      case 'backoffice-expanded':
        return this.active
          ? 'text-sm mr-3 text-[var(--color-text-300)] dark:text-[var(--color-text-300)]'
          : 'text-sm mr-3 text-[var(--color-text-100)] dark:text-[var(--color-text-100)] group-hover:text-[var(--color-text-300)]';
      case 'backoffice-collapsed':
        return this.active
          ? 'text-sm text-[var(--color-text-300)] dark:text-[var(--color-text-300)]'
          : 'text-sm text-[var(--color-text-100)] dark:text-[var(--color-text-100)] group-hover:text-[var(--color-text-300)]';
    }
  }

  get labelClass(): string {
    switch (this.variant) {
      case 'profile-settings':
        return this.active
          ? 'dark:text-[var(--color-white)] text-[var(--color-text-100)] text-sm font-medium'
          : 'dark:text-[var(--color-gray-400)] group-hover:text-[var(--color-text-100)] text-[var(--color-gray-600)] text-sm font-medium';
      case 'profile-collapsed':
        return '';
      case 'profile-sm':
        return this.active
          ? 'text-[var(--color-text-100)] dark:text-[var(--color-white)] text-sm'
          : 'text-[var(--color-gray-600)] group-hover:text-[var(--color-text-100)] dark:text-[var(--color-gray-400)] text-sm';
      case 'profile-base':
        return this.active
          ? 'text-[var(--color-text-100)] dark:text-[var(--color-white)] text-base'
          : 'text-[var(--color-gray-600)] group-hover:text-[var(--color-text-100)] dark:text-[var(--color-gray-400)] text-base';
      case 'backoffice-expanded':
        return this.active
          ? 'text-sm font-semibold text-[var(--color-text-300)] dark:text-[var(--color-text-300)]'
          : 'text-sm font-semibold text-[var(--color-text-100)] dark:text-[var(--color-text-100)] group-hover:text-[var(--color-text-300)]';
      case 'backoffice-collapsed':
        return '';
    }
  }
}
